import { computed, onBeforeUnmount, onMounted, ref, type Ref } from "vue";
import { isString } from "./var.check";

type ScrollNearEndAxis = "horizontal" | "vertical" | "both";

interface UseScrollNearEndOptions {
  threshold?: number;
  axis?: ScrollNearEndAxis;
}

const DEFAULT_SCROLL_NEAR_END_THRESHOLD = 0.9;

function normalizeUseScrollNearEndOptions(
  options?: number | UseScrollNearEndOptions,
): Required<UseScrollNearEndOptions> {
  if (typeof options === "number") {
    return {
      threshold: options,
      axis: "horizontal",
    };
  }

  return {
    threshold: options?.threshold ?? DEFAULT_SCROLL_NEAR_END_THRESHOLD,
    axis: options?.axis ?? "horizontal",
  };
}

export function useScrollNearEnd(
  elRef: Readonly<Ref<HTMLElement | null>>,
  options?: number | UseScrollNearEndOptions,
) {
  const { threshold, axis } = normalizeUseScrollNearEndOptions(options);
  const horizontalScrollRatio = ref(0);
  const verticalScrollRatio = ref(0);
  const hasHorizontalOverflow = ref(false);
  const hasVerticalOverflow = ref(false);
  const isHorizontallyScrolledNearEnd = computed(
    () => hasHorizontalOverflow.value && horizontalScrollRatio.value >= threshold,
  );
  const isVerticallyScrolledNearEnd = computed(
    () => hasVerticalOverflow.value && verticalScrollRatio.value >= threshold,
  );
  const scrollRatio = computed(() => {
    if (axis === "vertical") {
      return verticalScrollRatio.value;
    }

    if (axis === "both") {
      return Math.max(horizontalScrollRatio.value, verticalScrollRatio.value);
    }

    return horizontalScrollRatio.value;
  });
  const hasOverflow = computed(() => {
    if (axis === "vertical") {
      return hasVerticalOverflow.value;
    }

    if (axis === "both") {
      return hasHorizontalOverflow.value || hasVerticalOverflow.value;
    }

    return hasHorizontalOverflow.value;
  });
  const isScrolledNearEnd = computed(() => {
    if (axis === "vertical") {
      return isVerticallyScrolledNearEnd.value;
    }

    if (axis === "both") {
      return isHorizontallyScrolledNearEnd.value || isVerticallyScrolledNearEnd.value;
    }

    return isHorizontallyScrolledNearEnd.value;
  });

  const updateScrollMetrics = () => {
    const el = elRef.value;
    if (!el) return;

    const maxHorizontalScroll = el.scrollWidth - el.clientWidth;
    const maxVerticalScroll = el.scrollHeight - el.clientHeight;

    hasHorizontalOverflow.value = maxHorizontalScroll > 0;
    hasVerticalOverflow.value = maxVerticalScroll > 0;
    horizontalScrollRatio.value = maxHorizontalScroll > 0 ? el.scrollLeft / maxHorizontalScroll : 0;
    verticalScrollRatio.value = maxVerticalScroll > 0 ? el.scrollTop / maxVerticalScroll : 0;
  };

  let resizeObserver: ResizeObserver | null = null;

  onMounted(() => {
    updateScrollMetrics();
    if (elRef.value) {
      resizeObserver = new ResizeObserver(updateScrollMetrics);
      resizeObserver.observe(elRef.value);
    }
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
  });

  return {
    hasOverflow,
    hasHorizontalOverflow,
    hasVerticalOverflow,
    horizontalScrollRatio,
    isHorizontallyScrolledNearEnd,
    isScrolledNearEnd,
    isVerticallyScrolledNearEnd,
    scrollRatio,
    updateScrollMetrics,
    verticalScrollRatio,
  };
}

const defaultScrollIntoViewOptions = {
  behavior: "smooth",
  alignToTop: true,
};

function getElement(selector) {
  return isString(selector) ? document.querySelector(selector) : selector;
}

/**
 * TODO: scrollIntoView not well supported in all browser, smooth scroll not supported in Safari
 * @read https://caniuse.com/#feat=scrollintoview
 */
export default {
  scrollToView(selector, scrollOptions = {}) {
    const element = getElement(selector);
    const scrollIntoViewOptions = Object.assign({}, defaultScrollIntoViewOptions, scrollOptions);
    if (element) {
      element.scrollIntoView(scrollIntoViewOptions);
    }
  },
  waitForScrollEnd(container: Element, timeoutMs = 1500): Promise<void> {
    return new Promise((resolve) => {
      const listenTarget: EventTarget =
        container === document.scrollingElement ? document : container;
      let lastTop = container.scrollTop;
      let stableCount = 0;

      const finish = () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        listenTarget.removeEventListener("scrollend", finish);
        resolve();
      };

      const intervalId = setInterval(() => {
        if (container.scrollTop === lastTop) {
          stableCount += 1;
          if (stableCount >= 2) {
            finish();
          }
        } else {
          stableCount = 0;
          lastTop = container.scrollTop;
        }
      }, 100);
      const timeoutId = setTimeout(finish, timeoutMs);
      listenTarget.addEventListener("scrollend", finish);
    });
  },
  scrollToTopOfContainerWithHeaderOffset(element, container, additionalOffset = 0) {
    const headerOffset = 48;
    const offset =
      element.getBoundingClientRect().top -
      container.getBoundingClientRect().top +
      container.scrollTop -
      headerOffset -
      additionalOffset;
    container.scrollTop = offset;
  },
  scrollToTopWithHeaderOffset(element, container, additionalOffset = 0) {
    const headerOffset = 48;
    const offset =
      element.getBoundingClientRect().top + container.scrollTop - headerOffset - additionalOffset;
    container.scrollTop = offset;
  },
  smoothScrollToTopWithHeaderOffset(element, container, additionalOffset = 0) {
    const headerOffset = 48;
    const offset =
      element.getBoundingClientRect().top + container.scrollTop - headerOffset - additionalOffset;
    this.windowScrollTop(offset);
  },
  scrollToBottomWithOffset(element, container, additionalOffset = 0) {
    // subtracting client height would cause the top of the element to be placed at the bottom of the viewport
    const offset =
      container.scrollTop +
      element.getBoundingClientRect().top +
      additionalOffset -
      document.documentElement.clientHeight;
    container.scrollTop = offset;
  },
  scrollToBottomOfContainer(container, behavior: ScrollBehavior = "smooth") {
    container.scrollTo({
      top: container.scrollHeight,
      behavior: behavior,
    });
  },
  scrollToTopOfContainer(container, top = 0, behavior: ScrollBehavior = "smooth") {
    container.scrollTo({
      top: top,
      behavior: behavior,
    });
  },
  scrollLeftOfContainerWithOffset(element, container, additionalOffset = 0) {
    const offset = element.getBoundingClientRect().left + container.scrollLeft - additionalOffset;
    container.scrollLeft = offset;
  },
  // Scroll function for the window, which accepts the top coordinates as paramaters
  windowScrollTop(top = 0, behavior: ScrollBehavior = "smooth") {
    window.scrollTo({
      top: top,
      behavior: behavior,
    });
  },
  /**
   * Scroll activity or quiz activity page navigator until the current page is anchor to center.
   *
   * @param {HTMLElement} navigatorContainer        navigator parent element
   * @param {Number} selectedNavigatorLeftPosition  navigator offsetLeft
   * @param {Number} pageCenterPosition             navigatorContainer center position
   */
  scrollActivityPageNavigatorToCurrentPage(
    navigatorContainer,
    selectedNavigatorLeftPosition,
    pageCenterPosition,
  ) {
    if (selectedNavigatorLeftPosition > pageCenterPosition) {
      navigatorContainer.scrollTo({
        left: selectedNavigatorLeftPosition - pageCenterPosition,
        behavior: "smooth",
      });
    } else {
      navigatorContainer.scrollTo({
        left: 0,
        behavior: "smooth",
      });
    }
  },
  htmlNoScroll() {
    const html = document.documentElement;
    const count = Number(html.getAttribute("data-no-scroll-count")) || 0;

    if (count === 0) {
      html.classList.add("no-scroll");
    }

    html.setAttribute("data-no-scroll-count", String(count + 1));
  },

  htmlScrollResume() {
    const html = document.documentElement;
    const count = Number(html.getAttribute("data-no-scroll-count")) || 0;

    if (count <= 1) {
      html.classList.remove("no-scroll");
      html.removeAttribute("data-no-scroll-count");
    } else {
      html.setAttribute("data-no-scroll-count", String(count - 1));
    }
  },
};
